import { useQuery } from "@tanstack/react-query";
import { api, type UsageResponse } from "@/lib/api/client";
import { formatNumber } from "@/lib/utils/format-number";

// Refresh interval: 5 minutes
const REFRESH_INTERVAL = 5 * 60 * 1000;

/**
 * Hook to fetch and auto-refresh usage data
 *
 * Features:
 * - Auto-refreshes every 5 minutes
 * - Refetches on window focus
 * - Returns usage status, counts, and period info
 */
export function useUsage() {
  const query = useQuery({
    queryKey: ["usage"],
    queryFn: () => api.usage.get(),
    staleTime: 60_000, // Consider stale after 1 minute
    refetchInterval: REFRESH_INTERVAL,
    refetchOnWindowFocus: true,
  });

  return query;
}

/**
 * The plan an upgrade prompt offers, or null when there is nothing to offer.
 *
 * Null on Enterprise as well as Business: Enterprise's limits are its
 * contract's, so a tenant past one is not one plan away from more — it is a
 * conversation away (task 345).
 */
export function getNextTier(currentPlan: UsageResponse["plan"]): string | null {
  const tiers: Record<UsageResponse["plan"], string | null> = {
    free: "Team",
    team: "Professional",
    professional: "Business",
    business: null,
    enterprise: null,
  };
  return tiers[currentPlan];
}

/**
 * Format a number with locale-aware formatting
 */
export function formatReportCount(count: number): string {
  return formatNumber(count);
}
